import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  FaEye, FaUser, FaMapMarkerAlt, FaGraduationCap, FaBriefcase,
  FaSearch, FaClock, FaHeart, FaStar, FaComment, FaUserPlus,
  FaArrowLeft, FaArrowRight, FaDownload, FaChartBar, FaCalendar, FaUsers,
  FaSpinner, FaShare, FaLock, FaTimes, FaPhone
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { message } from 'antd';
import { BASE_API } from "../../Api/Api";
import { getUserActivePlans } from '../../Api/CoreApi';

const Visitors = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBy, setFilterBy] = useState('all');
  const [visitorsData, setVisitorsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [actionLoading, setActionLoading] = useState({});

  // ✅ Plan & Lock states
  const [activePlans, setActivePlans] = useState([]);
  const [viewedContacts, setViewedContacts] = useState([]);
  const [interestSentIds, setInterestSentIds] = useState([]);
  const [showLockModal, setShowLockModal] = useState(false);

  // ✅ Current User Gender & Caste States
  const [currentUserGender, setCurrentUserGender] = useState('');
  const [currentUserCaste, setCurrentUserCaste] = useState('');
  const [genderLoaded, setGenderLoaded] = useState(false);

  const token = localStorage.getItem('access_token');
  const userId = localStorage.getItem('user_id');
  const BASE_URL = BASE_API;

  // ===== HELPERS =====
  const hasActivePlan = () => activePlans.length > 0;
  const isProfileUnlocked = (uid) => viewedContacts.includes(String(uid));

  const canSendInterest = (uid) => {
    if (interestSentIds.includes(String(uid))) return true;
    if (isProfileUnlocked(String(uid))) return true;
    if (hasActivePlan()) return true;
    return false;
  };

  const getOppositeGender = (gender) => {
    if (!gender) return '';
    const g = gender.toLowerCase();
    if (g === 'male') return 'female';
    if (g === 'female') return 'male';
    return '';
  };

  const getGenderFilterInfo = () => {
    if (!currentUserGender) return null;
    const opposite = getOppositeGender(currentUserGender);
    return {
      showing: opposite === 'female' ? 'Female' : 'Male',
      icon: opposite === 'female' ? '👩' : '👨'
    };
  };

  // ===== FETCH CURRENT USER GENDER & CASTE =====
  const getCurrentUserProfile = async () => {
    if (!userId || !token) {
      setGenderLoaded(true);
      return;
    }

    try {
      // Pehle localStorage se check karo (fast)
      const savedGender = localStorage.getItem('user_gender');
      const savedCaste = localStorage.getItem('user_caste');
      
      if (savedGender) {
        setCurrentUserGender(savedGender);
      }
      if (savedCaste) {
        setCurrentUserCaste(savedCaste);
      }

      // Phir API se verify karo
      const response = await fetch(`${BASE_API}/UserData/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const dataArray = await response.json();
        const currentUser = Array.isArray(dataArray)
          ? dataArray.find(u => String(u?.user || u?.user_id || u?.id || u?.User_id) === String(userId))
          : null;

        if (currentUser) {
          if (currentUser?.gender) {
            setCurrentUserGender(currentUser.gender);
            localStorage.setItem('user_gender', currentUser.gender);
          }
          if (currentUser?.caste) {
            setCurrentUserCaste(currentUser.caste);
            localStorage.setItem('user_caste', currentUser.caste);
          }
        }
      }
      setGenderLoaded(true);
    } catch (error) {
      console.error("❌ Profile fetch error:", error);
      setGenderLoaded(true);
    }
  };

  // ===== FETCH ACTIVE PLANS =====
  const fetchUserPlans = async () => {
    try {
      const data = await getUserActivePlans();
      setActivePlans(data?.plans || []);
      setViewedContacts(data?.viewed_contacts || []);
    } catch (err) {
      console.error("❌ Plan fetch error:", err);
      setActivePlans([]);
      setViewedContacts([]);
    }
  };

  // ===== FETCH VISITORS =====
  const fetchVisitors = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BASE_URL}/visitors/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data?.data || [];
      setVisitorsData(data);
      setTotalPages(response.data?.totalPages || 1);
    } catch (error) {
      console.error("❌ Visitors fetch error:", error);
      message.error("Failed to load visitors");
      setVisitorsData([]);
    } finally {
      setLoading(false);
    }
  };

  // ===== FETCH STATS =====
  const fetchStats = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/visitors/stats/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats({
        total: response.data?.total || 0,
        today: response.data?.today || 0,
        week: response.data?.week || 0,
        month: response.data?.month || 0,
        new: response.data?.new || 0
      });
    } catch (error) {
      console.error("❌ Stats fetch error:", error);
      setStats({ total: 0, today: 0, week: 0, month: 0, new: 0 });
    }
  };

  // ===== INITIAL LOAD =====
  useEffect(() => {
    const init = async () => {
      await getCurrentUserProfile(); // ✅ Pehle gender load karo
      await fetchUserPlans();
      await fetchVisitors();
      await fetchStats();
    };
    init();

    try {
      const localIds = JSON.parse(localStorage.getItem('interest_sent_ids') || '[]');
      setInterestSentIds(localIds);
    } catch (e) {
      setInterestSentIds([]);
    }
  }, []);

  // ===== SEND INTEREST (🔒 PLAN + GENDER RESTRICTED) =====
  const sendInterest = async (targetUserId, targetGender) => {
    if (!targetUserId) {
      message.error("Invalid user");
      return;
    }

    // ✅ SAFETY CHECK: Same gender ko interest mat bhejo
    if (currentUserGender && targetGender) {
      if (currentUserGender.toLowerCase() === targetGender.toLowerCase()) {
        message.warning("⚠️ You can only send interest to opposite gender profiles!");
        return;
      }
    }

    if (interestSentIds.includes(String(targetUserId))) {
      message.info("Interest already sent!");
      return;
    }

    if (!canSendInterest(targetUserId)) {
      setShowLockModal(true);
      return;
    }

    setActionLoading(prev => ({ ...prev, [targetUserId]: 'interest' }));

    try {
      const response = await axios.post(
        `${BASE_URL}/interest/`,
        { to_user: targetUserId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );

      if (response.data) {
        const newIds = [...interestSentIds, String(targetUserId)];
        setInterestSentIds(newIds);
        localStorage.setItem('interest_sent_ids', JSON.stringify(newIds));
        message.success("💖 Interest sent successfully!");
      }
    } catch (error) {
      console.error("❌ Interest error:", error);
      if (error.response?.status === 403) {
        setShowLockModal(true);
      } else if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else {
        message.info("Interest already sent or failed!");
      }
    } finally {
      setActionLoading(prev => ({ ...prev, [targetUserId]: null }));
    }
  };

  // ===== SHORTLIST =====
  const handleShortlist = async (targetUserId) => {
    if (!targetUserId) { message.error("Invalid user"); return; }
    setActionLoading(prev => ({ ...prev, [targetUserId]: 'shortlist' }));
    try {
      await axios.post(
        `${BASE_URL}/shortlist/`,
        { target_user_id: targetUserId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );
      message.success("⭐ Shortlisted successfully!");
    } catch (error) {
      console.error("❌ Shortlist error:", error);
      if (error.response?.data?.message) message.error(error.response.data.message);
      else message.error("Failed to shortlist");
    } finally {
      setActionLoading(prev => ({ ...prev, [targetUserId]: null }));
    }
  };

  const handleChat = (userId) => {
    if (!userId) { message.error("Invalid user"); return; }
    navigate(`/messages?chat=${userId}`);
  };

  const handleViewProfile = (userId) => {
    if (!userId) { message.error("Invalid user"); return; }
    navigate(`/user-profile/${userId}`);
  };

  // ===== ✅ MAIN FILTER LOGIC (Gender + Caste + Others) =====
  const filteredVisitors = visitorsData
    .filter(item => {
      // ✅ 1. GENDER FILTER: Sirf opposite gender dikhao
      if (currentUserGender && item.gender) {
        const myGender = currentUserGender.toLowerCase();
        const visitorGender = item.gender.toLowerCase();
        // Agar same gender hai toh skip karo
        if (myGender === visitorGender) return false;
      }

      // ✅ 2. Search Filter
      const matchesSearch = item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.bid?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.profile_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.location?.toLowerCase().includes(searchTerm.toLowerCase());

      // ✅ 3. Manual gender filter (from dropdown - optional)
      if (filterBy === 'male' && item.gender?.toLowerCase() !== 'male') return false;
      if (filterBy === 'female' && item.gender?.toLowerCase() !== 'female') return false;

      // ✅ 4. Date based tabs
      if (activeTab === 'today') {
        const today = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
        if (item.visitedDate !== today) return false;
      }

      if (activeTab === 'week') {
        try {
          const dateParts = item.visitedDate.split(' ');
          if (dateParts.length >= 3) {
            const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            const month = monthNames.indexOf(dateParts[1]);
            const day = parseInt(dateParts[0]);
            const year = parseInt(dateParts[2]);
            const date = new Date(year, month, day);
            const today = new Date();
            const diffDays = Math.ceil(Math.abs(today - date) / (1000 * 60 * 60 * 24));
            if (diffDays > 7) return false;
          }
        } catch (e) { return false; }
      }

      if (activeTab === 'month') {
        try {
          const dateParts = item.visitedDate.split(' ');
          if (dateParts.length >= 3) {
            const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            const month = monthNames.indexOf(dateParts[1]);
            const day = parseInt(dateParts[0]);
            const year = parseInt(dateParts[2]);
            const date = new Date(year, month, day);
            const today = new Date();
            const diffDays = Math.ceil(Math.abs(today - date) / (1000 * 60 * 60 * 24));
            if (diffDays > 30) return false;
          }
        } catch (e) { return false; }
      }

      return matchesSearch;
    })
    // ✅ 5. SORTING: Same caste wale profiles ko top par lao
    .sort((a, b) => {
      if (!currentUserCaste) return 0;
      const myCaste = currentUserCaste.toLowerCase();
      const aCasteMatch = (a.caste || '').toLowerCase() === myCaste ? 1 : 0;
      const bCasteMatch = (b.caste || '').toLowerCase() === myCaste ? 1 : 0;
      return bCasteMatch - aCasteMatch; // Same caste first
    });

  const [stats, setStats] = useState({ total: 0, today: 0, week: 0, month: 0, new: 0 });

  // ===== LOCK MODAL =====
  const LockModal = () => (
    <div className="au-lock-overlay" onClick={() => setShowLockModal(false)}>
      <div className="au-lock-modal" onClick={(e) => e.stopPropagation()}>
        <button className="au-lock-close" onClick={() => setShowLockModal(false)}><FaTimes /></button>
        <div className="au-lock-icon">🔒</div>
        <h3 className="au-lock-title">Plan Required!</h3>
        <p className="au-lock-desc">You need an active plan to send interest.</p>
        <div className="au-lock-features">
          <div className="au-lock-feature"><FaHeart style={{ color: '#ff0055' }} /><span>Unlimited interests</span></div>
          <div className="au-lock-feature"><FaPhone style={{ color: '#ff0055' }} /><span>View contacts</span></div>
        </div>
        <button className="au-lock-btn-primary" onClick={() => { setShowLockModal(false); navigate('/recharge'); }}>🚀 Buy Plan</button>
        <button className="au-lock-btn-secondary" onClick={() => setShowLockModal(false)}>Maybe Later</button>
      </div>
    </div>
  );

  // ===== LOADING =====
  if (loading || !genderLoaded) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', flexDirection: 'column', gap: 16 }}>
        <FaSpinner size={48} style={{ color: '#ff0055', animation: 'spin 1s linear infinite' }} />
        <p style={{ color: '#888' }}>Loading visitors...</p>
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  const genderInfo = getGenderFilterInfo();

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800;900&display=swap');
* { font-family: 'Poppins', sans-serif !important; box-sizing: border-box; margin: 0; }
@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
@keyframes vsGlow { 0%,100% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } }
@keyframes vsShimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }

.vs-page { background: #fff; min-height: 100vh; padding: 20px 16px; }
.vs-wrap { max-width: 1280px; margin: 0 auto; }

/* Header */
.vs-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 22px; flex-wrap: wrap; gap: 12px; padding: 20px 22px; background: linear-gradient(135deg, #fff0f3, #ffe4ec); border: 2px solid #ffe4ec; border-radius: 18px; }
.vs-title { font-size: 24px; font-weight: 800; color: #1a0a10; margin: 0; display: flex; align-items: center; gap: 10px; }
.vs-title svg { color: #ff0055; }
.vs-subtitle { color: #888; font-size: 13.5px; margin: 4px 0 0; }
.vs-actions { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.vs-search-wrap { position: relative; }
.vs-search { padding: 10px 14px 10px 40px; border: 1.5px solid #ffc2d1; border-radius: 12px; font-size: 13px; outline: none; width: 240px; transition: all .3s ease; background: #fff; }
.vs-search:focus { border-color: #ff0055; box-shadow: 0 0 0 3px rgba(255,0,85,.08); }
.vs-search-icon { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); color: #ff4d88; font-size: 13px; }

/* Same Caste Badge */
.vs-same-caste-badge { position: absolute; top: 14px; left: 14px; background: linear-gradient(135deg, #10b981, #059669); color: #fff; font-size: 9px; padding: 3px 8px; border-radius: 999px; font-weight: 800; box-shadow: 0 4px 10px rgba(16,185,129,.28); z-index: 2; text-transform: uppercase; letter-spacing: 0.3px; }

/* Stats */
.vs-stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin-bottom: 22px; }
.vs-stat-card { background: #fff; border: 2px solid #ffe4ec; border-radius: 18px; padding: 18px; display: flex; align-items: center; gap: 14px; transition: all .3s ease; box-shadow: 0 4px 16px rgba(255,0,85,.04); position: relative; overflow: hidden; }
.vs-stat-card::before { content: ""; position: absolute; top: 0; left: 0; right: 0; height: 3px; background: linear-gradient(90deg, #ff0055, #ff4d88, #ff80aa); background-size: 200% 100%; animation: vsShimmer 3s linear infinite; }
.vs-stat-card:hover { transform: translateY(-4px); border-color: #ffb3c8; box-shadow: 0 12px 28px rgba(255,0,85,.1); }
.vs-stat-icon { width: 50px; height: 50px; border-radius: 14px; display: flex; align-items: center; justify-content: center; font-size: 20px; color: #fff; flex-shrink: 0; box-shadow: 0 6px 16px rgba(255,0,85,.15); }
.vs-stat-icon.total { background: linear-gradient(135deg, #ff0055, #ff4d88); }
.vs-stat-icon.today { background: linear-gradient(135deg, #ff4d88, #ff80aa); }
.vs-stat-icon.week { background: linear-gradient(135deg, #c2185b, #ff0055); }
.vs-stat-icon.new { background: linear-gradient(135deg, #ff80aa, #ffb3c8); }
.vs-stat-value { font-size: 24px; font-weight: 800; color: #1a0a10; margin: 0; }
.vs-stat-label { font-size: 12px; color: #888; margin: 2px 0 0; font-weight: 500; }

/* Tabs */
.vs-tabs { display: flex; gap: 4px; padding: 5px; background: #fff0f3; border: 2px solid #ffe4ec; border-radius: 14px; margin-bottom: 16px; flex-wrap: wrap; }
.vs-tab { padding: 10px 18px; border: none; border-radius: 10px; cursor: pointer; font-size: 13px; font-weight: 700; transition: all .25s ease; background: transparent; color: #888; display: flex; align-items: center; gap: 6px; }
.vs-tab:hover { color: #ff0055; background: rgba(255,0,85,.04); }
.vs-tab.active { background: linear-gradient(135deg, #ff0055, #ff4d88); color: #fff; box-shadow: 0 4px 14px rgba(255,0,85,.22); }
.vs-tab-badge { background: rgba(255,255,255,.25); color: #fff; font-size: 10.5px; padding: 2px 8px; border-radius: 999px; font-weight: 700; }
.vs-tab:not(.active) .vs-tab-badge { background: #ff0055; color: #fff; }

/* Visitor Cards */
.vs-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 18px; }
.vs-card { background: #fff; border: 2px solid #ffe4ec; border-radius: 20px; padding: 20px; position: relative; transition: all .35s ease; box-shadow: 0 4px 16px rgba(255,0,85,.04); overflow: hidden; }
.vs-card::before { content: ""; position: absolute; top: 0; left: 0; right: 0; height: 50px; background: linear-gradient(135deg, #fff0f3, #ffe4ec); border-radius: 20px 20px 0 0; }
.vs-card:hover { transform: translateY(-6px); border-color: #ff80aa; box-shadow: 0 16px 36px rgba(255,0,85,.12); }
.vs-new-badge { position: absolute; top: 14px; right: 14px; background: linear-gradient(135deg, #ff0055, #ff4d88); color: #fff; font-size: 10px; padding: 4px 12px; border-radius: 999px; font-weight: 800; box-shadow: 0 4px 10px rgba(255,0,85,.28); z-index: 2; }

.vs-profile-row { display: flex; align-items: center; gap: 14px; margin-bottom: 14px; position: relative; z-index: 2; }
.vs-avatar-wrap { position: relative; flex-shrink: 0; padding: 3px; background: linear-gradient(135deg, #ff0055, #ff4d88, #ff80aa, #ff0055); background-size: 200% 200%; animation: vsGlow 3s ease infinite; border-radius: 50%; box-shadow: 0 8px 20px rgba(255,0,85,.2); }
.vs-avatar { width: 58px; height: 58px; border-radius: 50%; border: 3px solid #fff; object-fit: cover; display: block; }
.vs-online-dot { position: absolute; bottom: 3px; right: 3px; width: 14px; height: 14px; border-radius: 50%; background: #4caf50; border: 2.5px solid #fff; box-shadow: 0 2px 6px rgba(76,175,80,.3); }
.vs-name { font-size: 16px; font-weight: 800; color: #1a0a10; margin: 0; }
.vs-bid { font-size: 11px; color: #ff0055; font-weight: 800; margin: 3px 0; padding: 2px 10px; border-radius: 999px; background: linear-gradient(135deg, #fff0f3, #ffe4ec); display: inline-block; border: 1px solid #ffd3df; }
.vs-online-row { display: flex; align-items: center; gap: 5px; margin-top: 3px; }
.vs-online-text { font-size: 11px; font-weight: 600; }

.vs-details { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; padding: 12px; background: #fff; border: 1.5px solid #ffe4ec; border-radius: 14px; margin-bottom: 12px; position: relative; z-index: 2; }
.vs-detail { display: flex; align-items: center; gap: 6px; font-size: 11.5px; color: #666; font-weight: 500; padding: 4px 8px; background: linear-gradient(135deg, #fffafc, #fff5f7); border-radius: 8px; }
.vs-detail svg { color: #ff4d88; font-size: 10px; flex-shrink: 0; }
.vs-detail.heart svg { color: #ff0055; }
.vs-detail.full { grid-column: 1 / -1; }

.vs-visit-count { display: inline-flex; align-items: center; gap: 6px; font-size: 11px; color: #ff0055; font-weight: 800; padding: 5px 14px; background: linear-gradient(135deg, #fff0f3, #ffe4ec); border-radius: 999px; border: 1.5px solid #ffd3df; margin-bottom: 14px; position: relative; z-index: 2; }
.vs-visit-count svg { color: #ff0055; }

.vs-actions-row { display: flex; gap: 8px; position: relative; z-index: 2; }
.vs-action-btn { flex: 1; padding: 9px; border: none; border-radius: 12px; font-size: 12px; font-weight: 700; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; gap: 5px; transition: all .25s ease; }
.vs-action-btn:hover:not(:disabled) { transform: translateY(-2px); }
.vs-action-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.vs-btn-interest { background: linear-gradient(135deg, #ff0055, #ff4d88); color: #fff; box-shadow: 0 6px 16px rgba(255,0,85,.2); }
.vs-btn-interest:hover:not(:disabled) { box-shadow: 0 10px 22px rgba(255,0,85,.28); }
.vs-btn-interest.sent { background: #10b981; color: #fff; }
.vs-btn-interest.locked { background: #e2e8f0; color: #64748b; }
.vs-btn-shortlist { background: #fff; color: #ff0055; border: 1.5px solid #ff0055 !important; }
.vs-btn-shortlist:hover:not(:disabled) { background: linear-gradient(135deg, #ff0055, #ff4d88); color: #fff; border-color: #ff0055 !important; }

/* Plan Banner */
.vs-plan-banner { display: flex; align-items: center; gap: 12px; padding: 14px 18px; background: linear-gradient(135deg,#fff0f3,#ffe4ec); border: 2px solid #ffd3df; border-radius: 14px; margin-bottom: 18px; flex-wrap: wrap; }
.vs-plan-banner-text { flex: 1; min-width: 0; }
.vs-plan-banner-text p { margin: 0; font-size: 13px; font-weight: 800; color: #c2185b; }

/* Empty */
.vs-empty { grid-column: 1/-1; padding: 60px 24px; text-align: center; background: linear-gradient(135deg, #fff, #fff5f7); border: 2px solid #ffe4ec; border-radius: 22px; }
.vs-empty-icon { width: 85px; height: 85px; border-radius: 50%; background: linear-gradient(135deg, #fff0f3, #ffe4ec); display: flex; align-items: center; justify-content: center; margin: 0 auto 20px; font-size: 34px; color: #ff0055; box-shadow: 0 8px 22px rgba(255,0,85,.1); }
.vs-empty-title { font-size: 22px; font-weight: 800; color: #c2185b; margin: 0 0 10px; }
.vs-empty-sub { color: #888; margin: 0 0 22px; font-size: 14px; line-height: 1.6; }
.vs-empty-btn { padding: 12px 30px; border-radius: 999px; border: none; background: linear-gradient(135deg, #ff0055, #ff4d88); color: #fff; font-size: 14px; font-weight: 700; cursor: pointer; box-shadow: 0 8px 22px rgba(255,0,85,.22); transition: all .3s ease; }
.vs-empty-btn:hover { transform: translateY(-2px); box-shadow: 0 12px 28px rgba(255,0,85,.3); }

/* Pagination */
.vs-pagination { display: flex; justify-content: space-between; align-items: center; margin-top: 24px; padding: 18px 4px; flex-wrap: wrap; gap: 10px; border-top: 2px solid #fff0f3; }
.vs-page-info { font-size: 13px; color: #888; }
.vs-page-info strong { color: #ff0055; font-weight: 800; }
.vs-page-btns { display: flex; gap: 6px; flex-wrap: wrap; }
.vs-page-btn { min-width: 36px; height: 36px; border-radius: 10px; border: 1.5px solid #ffe4ec; background: #fff; color: #888; cursor: pointer; font-weight: 600; font-size: 13px; display: flex; align-items: center; justify-content: center; transition: all .25s ease; padding: 0 10px; }
.vs-page-btn:hover:not(:disabled) { border-color: #ff0055; color: #ff0055; }
.vs-page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.vs-page-btn.active { background: linear-gradient(135deg, #ff0055, #ff4d88); color: #fff; border-color: #ff0055; box-shadow: 0 4px 12px rgba(255,0,85,.2); }

/* Lock Modal */
.au-lock-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.6); backdrop-filter: blur(4px); display: flex; align-items: center; justify-content: center; z-index: 9999; padding: 20px; animation: fadeIn 0.2s ease; }
@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
.au-lock-modal { background: #fff; border-radius: 24px; padding: 32px 24px; max-width: 380px; width: 100%; text-align: center; position: relative; box-shadow: 0 20px 60px rgba(0,0,0,0.3); animation: popIn 0.3s ease; }
@keyframes popIn { from { transform: scale(0.9); opacity: 0; } to { transform: scale(1); opacity: 1; } }
.au-lock-close { position: absolute; top: 14px; right: 14px; background: #f8fafc; border: none; width: 32px; height: 32px; border-radius: 50%; cursor: pointer; color: #64748b; display: flex; align-items: center; justify-content: center; font-size: 12px; transition: all 0.2s ease; }
.au-lock-close:hover { background: #ff0055; color: #fff; }
.au-lock-icon { font-size: 60px; margin-bottom: 16px; }
.au-lock-title { font-size: 22px; font-weight: 800; color: #1a0a10; margin: 0 0 10px; }
.au-lock-desc { font-size: 14px; color: #666; margin: 0 0 24px; line-height: 1.5; }
.au-lock-features { display: flex; flex-direction: column; gap: 10px; margin-bottom: 24px; padding: 16px; background: #fff5f8; border: 1.5px solid #ffe4ec; border-radius: 14px; }
.au-lock-feature { display: flex; align-items: center; gap: 10px; font-size: 13px; font-weight: 600; color: #333; }
.au-lock-btn-primary { width: 100%; padding: 14px; border: none; border-radius: 999px; background: linear-gradient(135deg,#ff0055,#ff4d88); color: #fff; font-size: 14px; font-weight: 800; cursor: pointer; margin-bottom: 8px; box-shadow: 0 6px 18px rgba(255,0,85,0.3); transition: all 0.25s ease; }
.au-lock-btn-primary:hover { transform: translateY(-2px); box-shadow: 0 10px 24px rgba(255,0,85,0.4); }
.au-lock-btn-secondary { width: 100%; padding: 12px; border: 1.5px solid #e2e8f0; border-radius: 999px; background: #fff; color: #64748b; font-size: 13px; font-weight: 700; cursor: pointer; transition: all 0.25s ease; }
.au-lock-btn-secondary:hover { border-color: #cbd5e1; background: #f8fafc; }

/* Responsive */
@media (max-width: 1100px) { .vs-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 1024px) { .vs-stats-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 768px) {
  .vs-header { flex-direction: column; align-items: stretch; }
  .vs-search { width: 100%; }
  .vs-tabs { flex-wrap: wrap; }
  .vs-tab { flex: 1; justify-content: center; }
  .vs-grid { grid-template-columns: 1fr; }
  .vs-actions-row { flex-direction: column; }
  .vs-action-btn { width: 100%; }
}
@media (max-width: 480px) {
  .vs-stats-grid { grid-template-columns: 1fr 1fr; }
  .vs-page { padding: 14px 10px; }
}
      `}</style>

      {showLockModal && <LockModal />}

      <div className="vs-page">
        <div className="vs-wrap">

          {/* Header */}
          <div className="vs-header">
            <div>
              <h1 className="vs-title"><FaEye /> Profile Visitors</h1>
              <p className="vs-subtitle">
                See who viewed your profile 💖
                {genderInfo && (
                  <span style={{ marginLeft: 8, color: '#ff0055', fontWeight: 700 }}>
                    · {genderInfo.icon} Showing {genderInfo.showing} profiles
                  </span>
                )}
              </p>
            </div>
            <div className="vs-actions">
              <div className="vs-search-wrap">
                <FaSearch className="vs-search-icon" />
                <input
                  type="text"
                  placeholder="Search by name, ID, location..."
                  className="vs-search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="vs-stats-grid">
            <div className="vs-stat-card">
              <div className="vs-stat-icon total"><FaUsers /></div>
              <div><p className="vs-stat-value">{stats.total}</p><p className="vs-stat-label">Total Visitors</p></div>
            </div>
            <div className="vs-stat-card">
              <div className="vs-stat-icon today"><FaCalendar /></div>
              <div><p className="vs-stat-value">{stats.today}</p><p className="vs-stat-label">Today</p></div>
            </div>
            <div className="vs-stat-card">
              <div className="vs-stat-icon week"><FaChartBar /></div>
              <div><p className="vs-stat-value">{stats.week}</p><p className="vs-stat-label">This Week</p></div>
            </div>
            <div className="vs-stat-card">
              <div className="vs-stat-icon new"><FaUserPlus /></div>
              <div><p className="vs-stat-value">{stats.new}</p><p className="vs-stat-label">New Visitors</p></div>
            </div>
          </div>

          {/* Tabs */}
          <div className="vs-tabs">
            <button className={`vs-tab ${activeTab === 'all' ? 'active' : ''}`} onClick={() => setActiveTab('all')}>All Visitors</button>
            <button className={`vs-tab ${activeTab === 'today' ? 'active' : ''}`} onClick={() => setActiveTab('today')}>
              <FaClock size={12} /> Today <span className="vs-tab-badge">{stats.today}</span>
            </button>
            <button className={`vs-tab ${activeTab === 'week' ? 'active' : ''}`} onClick={() => setActiveTab('week')}>
              <FaCalendar size={12} /> Week <span className="vs-tab-badge">{stats.week}</span>
            </button>
            <button className={`vs-tab ${activeTab === 'month' ? 'active' : ''}`} onClick={() => setActiveTab('month')}>
              <FaChartBar size={12} /> Month <span className="vs-tab-badge">{stats.month}</span>
            </button>
          </div>

          {!hasActivePlan() && (
            <div className="vs-plan-banner">
              <div style={{ fontSize: 28 }}>🔒</div>
              <div className="vs-plan-banner-text">
                <p>Purchase a plan to send interest to your visitors!</p>
              </div>
              <button onClick={() => navigate('/recharge')} style={{ padding: '8px 16px', background: 'linear-gradient(135deg,#ff0055,#ff4d88)', color: '#fff', border: 'none', borderRadius: 999, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                Buy Plan
              </button>
            </div>
          )}

          {filteredVisitors.length > 0 ? (
            <div className="vs-grid">
              {filteredVisitors.map((v) => {
                const userIdKey = v.bid;
                const displayId = v.profile_id || v.bid;
                const isLoading = actionLoading[userIdKey];
                const isInterestSent = interestSentIds.includes(String(userIdKey));
                const canSend = canSendInterest(userIdKey);
                const isSameCaste = currentUserCaste && v.caste && 
                  currentUserCaste.toLowerCase() === v.caste.toLowerCase();

                return (
                  <div key={v.id} className="vs-card">
                    {isSameCaste && <span className="vs-same-caste-badge">✓ Same Caste</span>}
                    {v.isNew && !isSameCaste && <span className="vs-new-badge">⭐ New</span>}

                    <div className="vs-profile-row" onClick={() => handleViewProfile(userIdKey)} style={{ cursor: 'pointer' }}>
                      <div className="vs-avatar-wrap">
                        <img
                          src={v.image || `https://ui-avatars.com/api/?name=${v.name}&background=ff0055&color=fff&size=128`}
                          alt={v.name}
                          className="vs-avatar"
                          onError={(e) => {
                            e.target.src = `https://ui-avatars.com/api/?name=${v.name}&background=ff0055&color=fff&size=128`;
                          }}
                        />
                        {v.online && <span className="vs-online-dot"></span>}
                      </div>
                      <div>
                        <p className="vs-name">{v.name}</p>
                        <p className="vs-bid">ID: {displayId}</p>
                        <div className="vs-online-row">
                          <span style={{ width: 8, height: 8, borderRadius: '50%', background: v.online ? '#4caf50' : '#bbb' }}></span>
                          <span className="vs-online-text" style={{ color: v.online ? '#4caf50' : '#999' }}>
                            {v.online ? 'Online' : 'Offline'}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="vs-details">
                      <span className="vs-detail"><FaUser size={11} /> {v.age} Yrs</span>
                      <span className="vs-detail">{v.height}</span>
                      <span className="vs-detail full"><FaMapMarkerAlt size={11} /> {v.location}</span>
                      <span className="vs-detail"><FaGraduationCap size={11} /> {v.education}</span>
                      <span className="vs-detail"><FaBriefcase size={11} /> {v.occupation}</span>
                      <span className="vs-detail full"><FaClock size={11} /> {v.visitedDate} • {v.visitedTime}</span>
                      <span className="vs-detail heart full"><FaHeart size={11} /> {v.matchPercentage}% Match</span>
                    </div>

                    <div className="vs-visit-count">
                      <FaEye size={11} /> Visited {v.visitCount} {v.visitCount > 1 ? 'times' : 'time'}
                    </div>

                    <div className="vs-actions-row">
                      <button
                        className={`vs-action-btn vs-btn-interest ${isInterestSent ? 'sent' : !canSend ? 'locked' : ''}`}
                        onClick={() => sendInterest(userIdKey, v.gender)}
                        disabled={isLoading === 'interest' || isInterestSent}
                      >
                        {isLoading === 'interest' ? (
                          <FaSpinner size={10} style={{ animation: 'spin 1s linear infinite' }} />
                        ) : isInterestSent ? (
                          <>✅ Sent</>
                        ) : !canSend ? (
                          <><FaLock size={10} /> Locked</>
                        ) : (
                          <><FaHeart size={10} /> Interest</>
                        )}
                      </button>

                      <button
                        className="vs-action-btn vs-btn-shortlist"
                        onClick={() => handleShortlist(userIdKey)}
                        disabled={isLoading === 'shortlist'}
                      >
                        {isLoading === 'shortlist' ? 'Saving...' : <><FaStar size={10} /> Save</>}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="vs-empty">
              <div className="vs-empty-icon"><FaEye /></div>
              <h3 className="vs-empty-title">No Visitors Found</h3>
              <p className="vs-empty-sub">
                {activeTab === 'today' ? "No one has visited your profile today yet."
                  : activeTab === 'week' ? "No visitors found this week."
                    : activeTab === 'month' ? "No visitors found this month."
                      : genderInfo ? `No ${genderInfo.showing} visitors found matching your criteria.`
                        : "No visitors match your search criteria."}
              </p>
              <button className="vs-empty-btn" onClick={() => navigate('/matches')}>💖 Find Matches</button>
            </div>
          )}

          {filteredVisitors.length > 0 && (
            <div className="vs-pagination">
              <div className="vs-page-info">
                Showing <strong>{filteredVisitors.length}</strong> of <strong>{visitorsData.length}</strong> visitors
              </div>
              <div className="vs-page-btns">
                <button className="vs-page-btn" onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} disabled={currentPage === 1}>
                  <FaArrowLeft size={10} />
                </button>
                {Array.from({ length: totalPages }, (_, index) => (
                  <button key={index} className={`vs-page-btn ${currentPage === index + 1 ? "active" : ""}`} onClick={() => setCurrentPage(index + 1)}>
                    {index + 1}
                  </button>
                ))}
                <button className="vs-page-btn" onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} disabled={currentPage === totalPages}>
                  <FaArrowRight size={10} />
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
    </>
  );
};

export default Visitors;